import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { OptionType } from '../../../utils/type';
import { Dayjs } from 'dayjs';
import {
  EmployeeListType,
  getAllEmployeeMinimalList,
  getIfspById,
} from '../../../services/ifspClient/IfspApi';
import { LoaderContext, LoaderContextType } from '../../../layouts/AppSidebar';
import { Box } from '@mui/system';
import SnackBarComponent from '../../../components/formlib/SnackBarComponent';
import { AlertColor } from '@mui/material';
import ThomProgram from '../../../assets/images/thomchildlogo.svg';
import { ApiError, isCustomError } from '../../../services/ApiResponseHandler';
import _, { cloneDeep } from 'lodash';
import {
  LookUpContext,
  LookUpContextType,
} from '../../../context/LookUpContextProvider';
import { UnSavedChangesContext } from '../../../context/UnSavedChangesProvider';
type IfspContextType = {
  children: ReactNode;
};

export type RoutineType = {
  [key: string]: {
    value: string;
    comments: string;
  };
};

export type CustomRoutineType = {
  name: string;
  value: string;
  comments: string;
};
export type ReviewedOnType = {
  reviewedDate: string | null | Dayjs;
  reviewedByName: string;
  reviewedByDiscipline: string;
};

export type IfspTeamMembersType = {
  id: string;
  name: string;
  phone: string;
  email: string;
  startDate?: string | null | Dayjs;
  endDate?: string | null | Dayjs;
  discipline?: string | null;
  jobDescription?: string | null;
  role: string;
};
export type ContactsType = {
  id: string;
  name: string;
  phone: string;
  email: string;
  isPrimary: boolean;
  isGuardian: boolean;
  isLegal: boolean;
  type: string;
  relationshipToClient: string;
  address: string;
  startDate?: string | null;
  endDate?: string | null;
};

export type ParentsType = {
  id: string;
  name: string;
  role: string;
  email: string;
  phone: string;
};

export type TeamMembersType = {
  id: string;
  name: string;
  role: string;
  discipline?: string | null;
  email: string;
  phone: string;
  startDate?: string | null | Dayjs;
  endDate?: string | null | Dayjs;
};

export type OthersType = {
  name: string;
  jobDescription: string;
  startDate?: string | null | Dayjs;
  endDate?: string | null | Dayjs;
};

export type EasiestHardestTimesType = {
  routineName: string;
  reason: string;
};

type HealthSummaryType = {
  dateOfEvaluation: string | null | Dayjs;
  teamMember: {
    name: string;
    memberName: string;
    discipline: string;
  }[];
  evaluationResults: string;
  healthHistory: string;
  currentHealthStatus: string;
  additionalInfo: string;
  sessionReviewedDate: string | null | Dayjs;
};

type OutcomesProgressReviewType = {
  outcomeId: string;
  startDate: string | null | Dayjs;
  endDate: string | null | Dayjs;
  concern: string;
  outcome: string;
  measurableProcedure: string;
  targetDate: string;
  isRecordSubmitted: boolean;
  tbrOutcomeId: number;
  tbrUpdateRequired: boolean;
};

type OutcomesProgressInformationType = {
  date: string | null | Dayjs;
  progressDesc: string;
  progressRating: string;
};

type ChildIfspOutcomeType = {
  outcomesProgressReview: OutcomesProgressReviewType;
  outcomesProgressInformation: OutcomesProgressInformationType[];
};

type FamilyConcernsType = {
  concern: string;
  priorities: string;
  materialResource: string;
  infoResource: string;
  supportiveResource: string;
  sessionReviewedDate: string | null | Dayjs;
};

export type DevelopmentalSummaryProgressReview = {
  adaptive: string;
  personalSocial: string;
  communication: string;
  motor: string;
  cognition: string;
};

export type DevelopmentalSummaryType = {
  developmentalSummaryProgressReview: DevelopmentalSummaryProgressReview[];
  sessionReviewedDate: string | null | Dayjs;
};

type EarlyInterventionServiceType = {
  servicePlanId: string;
  eiService: string;
  providedBy: string;
  locationOfService: string;
  length: string;
  period: string;
  frequency: string;
  intensity: string;
  duration: string;
  startDate: string | null | Dayjs;
  endDate: string | null | Dayjs;
  reasonIfLate: string;
  isRecordSubmitted: boolean;
  tbrServicePlanId: number;
  tbrUpdateRequired: boolean;
};

type ServiceDeliveryPlanType = {
  earlyInterventionService: EarlyInterventionServiceType[];
  notes: string;
  justification: string;
  otherNeededService: string;
  stepsToTake: string;
};

type TransitionPlanType = {
  isTransitionPlanDone: string;
  isTpcMeetingDone: string;
  isConsentDoneToShareInfoToLea: string;
  transitionPlanDate: string | null | Dayjs;
  noTransitionPlanReason: string;
  needs: string;
  leaEligible: string;
  notify: string;
  consentShare: boolean;
  consentDate: string | null | Dayjs;
  outsideTPCReason: string;
  meetingDate: string | null | Dayjs;
  noTPCReason: string;
  sectionDetermine: boolean;
  specify: string;
  makeReferral: boolean;
  date: string | null | Dayjs;
  tpc: boolean;
  leaInvited: string;
  repParticipate: string;
  participatedNames: string;
  noReferralMade: boolean;
  seaStatus: string;
  leaOptOutDate: string | null | Dayjs;
};

type ConsentForChangeType = {
  changes: string;
  isConsentSkipped: boolean;
  isAdminAmendment: boolean;
  isEligibleUntilThirdBirthday: string;
  eligibleTool: OptionType;
  isEligibleFor: string;
  assessmentCompletionDate: string | null | Dayjs;
  reEstablishedDate: string | null | Dayjs;
  parentInitial: OptionType;
  consentType: string;
  sessionReviewedDate: string | null | Dayjs;
  participated: boolean;
  agreeDisagree: string;
  exceptionList: string;
  employeeSignature: {
    id: string;
    isUpdated: boolean;
    signature: string;
    verbalConsent: boolean;
    date: string | null | Dayjs;
    partitionKey: string;
    rowKey: string;
  };
  parentSignature: {
    id: string;
    isUpdated: boolean;
    signature: string;
    verbalConsent: boolean;
    date: string | null | Dayjs;
    partitionKey: string;
    rowKey: string;
  }[];
  consent: boolean;
  consentSignature: {
    id: string;
    isUpdated: boolean;
    signature: string;
    verbalConsent: boolean;
    date: string | null | Dayjs;
    partitionKey: string;
    rowKey: string;
  }[];
};

export type IfspContextDataType = {
  programDetails: {
    name: string;
    imageUrl: string;
    email: string;
    phoneNumber: string;
    faxNumber: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
    website: string;
  };
  client: {
    name: string;
    sex: string;
    dphId: string;
    dob: string;
  };
  reasonForDelay: {
    id: string;
    label: string;
  };
  reviewedList: ReviewedOnType[];
  ifspTeam: {
    contact: ContactsType[];
    parents: ParentsType[];
    teamMember: TeamMembersType[];
    others: OthersType[];
    forms: {
      dailyRoutines: {
        routines: RoutineType;
        customRoutines: CustomRoutineType[];
        easiestTimes: EasiestHardestTimesType[];
        hardestTimes: EasiestHardestTimesType[];
        sessionReviewedDate: string | null | Dayjs;
      }[];
      healthSummary: HealthSummaryType[];
      childIfspOutcomes: ChildIfspOutcomeType[];
      familyConcerns: FamilyConcernsType[];
      developmentalSummary: DevelopmentalSummaryType[];
      serviceDeliveryPlan: ServiceDeliveryPlanType[];
      transitionPlan: TransitionPlanType;
      transitionPreparation: {
        transitionPreparationDate: string | null | Dayjs;
        changesComingUp: string;
        preparationHelp: string;
        supports: string;
        communityOpportunities: string;
        serviceChangesNeeded: string;
      };
      consentForChangeForSDP: ConsentForChangeType[];
    };
  };
  id: string | null;
  completedByRole: string;
  completedBy: OptionType;
  ifspMeetingDate: Dayjs | string | null;
  primarySettingCode: string;
  primarySettingCodeJustification: string;
  ifspSignedDate: Dayjs | string | null;
  ifspDueDate: Dayjs | string | null;
  isLocked: boolean;
  lockWithoutConsentSignature: boolean;
  isReportUpdated: boolean;
  evaluationDate: Dayjs | null | string;
  notificationDate: Dayjs | null | string;
  dischargeDate: Dayjs | null | string;
  initialEvalNotificationDate: Dayjs | null | string;
  referralBeginDate: Dayjs | null | string;
  referralContactDate: Dayjs | null | string;
  selectedLanguage: string;
  isEIServiceOrOutcomesInfoDeleted: boolean;
};

export const initialRoutine = {
  wakingUp: {
    name: 'Waking up',
    value: '',
    comments: '',
  },
  mealTime: {
    name: 'Meal time',
    value: '',
    comments: '',
  },
  napTime: {
    name: 'Nap time',
    value: '',
    comments: '',
  },
  playTime: {
    name: 'Play time',
    value: '',
    comments: '',
  },
  downTime: {
    name: 'Down time/hanging out',
    value: '',
    comments: '',
  },
  bathTime: {
    name: 'Bath time',
    value: '',
    comments: '',
  },
  bedTime: {
    name: 'Bed time',
    value: '',
    comments: '',
  },
  familyActivities: {
    name: 'Family activities',
    value: '',
    comments: '',
  },
  socialGatherings: {
    name: 'Social and community gatherings',
    value: '',
    comments: '',
  },
  comingFromHome: {
    name: 'Coming and going from home',
    value: '',
    comments: '',
  },
  dropOff: {
    name: 'Drop off/Pick up',
    value: '',
    comments: '',
  },
  runningErrands: {
    name: 'Running errands',
    value: '',
    comments: '',
  },
  workSchedule: {
    name: 'Work/School schedule',
    value: '',
    comments: '',
  },
  childCare: {
    name: 'Child care',
    value: '',
    comments: '',
  },
  doctorAppointment: {
    name: "Doctor's (or other) appointments",
    value: '',
    comments: '',
  },
};

const IfspContext = createContext<{
  ifsp: IfspContextDataType;
  initialIfspForReports: IfspContextDataType;
  initialIfsp: IfspContextDataType;
  updateIfspContext: (data: IfspContextDataType) => void;
  updateIfspForReport: (data: IfspContextDataType) => void;
  updateInitialIfspContext: (data: IfspContextDataType) => void;
  updateIfspTouched: (touched: boolean) => void;
  getIfsp?: (clientId: string) => void;
  ifspTouched: boolean;
  employeeData: EmployeeListType[];
  updateIsTransitionDateReqired: (data: boolean) => void;
  updateIsLeaReferralDateRequired: (data: boolean) => void;
  updateIsTpcMeetingDateRequired: (data: boolean) => void;
  updateIsOptOutNotificationDateRequired: (data: boolean) => void;
  updateTransitionOutOfRange: (data: boolean) => void;
  UpdateLeaReferralDateOutOfRange: (data: boolean) => void;
  UpdateTpcMeetingDateOutOfRange: (data: boolean) => void;
  updateOutComesFieldFlag: (data: boolean) => void;
  updateOutComesStartDateFlag: (data: boolean) => void;
  updateOutComesEndDateFlag: (data: boolean) => void;
  isTransitionDateReqired: boolean;
  isTpcMeetingDateRequired: boolean;
  isLeaReferralDateRequired: boolean;
  isOptOutNotificationDateRequired: boolean;
  transitionDateOutOfRange: boolean;
  leaReferralDateOutOfRange: boolean;
  tpcMeetingDateOutOfRange: boolean;
  primarySettingCode: OptionType[];
  reasonData: OptionType[];
  activeReasonData: OptionType[];
  isDailyRoutineUpdated: boolean;
  isFamilyConcernsUpdated: boolean;
  isOutComeEndDateHasIssue: boolean;
  isOutComeStartDateHasIssue: boolean;
  isHealthAndEligibilityUpdated: boolean;
  isDevelopmentSummaryUpdated: boolean;
  isAllOutComesUpdated: boolean;
}>({
  ifsp: {
    programDetails: {
      name: '',
      imageUrl: '',
      email: '',
      phoneNumber: '',
      faxNumber: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      website: '',
    },
    client: {
      name: '',
      sex: '',
      dphId: '',
      dob: '',
    },
    reasonForDelay: {
      id: '',
      label: '',
    },
    reviewedList: [],
    ifspTeam: {
      contact: [],
      parents: [],
      teamMember: [],
      others: [],
      forms: {
        dailyRoutines: [
          {
            routines: initialRoutine,
            customRoutines: [],
            easiestTimes: [
              { routineName: '', reason: '' },
              { routineName: '', reason: '' },
              { routineName: '', reason: '' },
            ],
            hardestTimes: [
              { routineName: '', reason: '' },
              { routineName: '', reason: '' },
              { routineName: '', reason: '' },
            ],
            sessionReviewedDate: '',
          },
        ],
        healthSummary: [
          {
            dateOfEvaluation: null,
            teamMember: [],
            evaluationResults: '',
            healthHistory: '',
            currentHealthStatus: '',
            additionalInfo: '',
            sessionReviewedDate: '',
          },
        ],
        childIfspOutcomes: [
          {
            outcomesProgressReview: {
              outcomeId: '',
              startDate: null,
              endDate: null,
              concern: '',
              outcome: '',
              measurableProcedure: '',
              targetDate: '',
              isRecordSubmitted: false,
              tbrOutcomeId: 0,
              tbrUpdateRequired: true,
            },
            outcomesProgressInformation: [
              {
                date: null,
                progressDesc: '',
                progressRating: '',
              },
            ],
          },
        ],
        familyConcerns: [
          {
            concern: '',
            priorities: '',
            materialResource: '',
            infoResource: '',
            supportiveResource: '',
            sessionReviewedDate: '',
          },
        ],
        developmentalSummary: [
          {
            developmentalSummaryProgressReview: [
              {
                adaptive: '',
                cognition: '',
                communication: '',
                motor: '',
                personalSocial: '',
              },
            ],
            sessionReviewedDate: '',
          },
        ],
        serviceDeliveryPlan: [
          {
            earlyInterventionService: [],
            notes: '',
            justification: '',
            otherNeededService: '',
            stepsToTake: '',
          },
        ],
        transitionPlan: {
          consentDate: null,
          isTransitionPlanDone: '',
          isTpcMeetingDone: '',
          isConsentDoneToShareInfoToLea: '',
          leaEligible: '',
          noTPCReason: '',
          noTransitionPlanReason: '',
          outsideTPCReason: '',
          seaStatus: '',
          leaOptOutDate: null,
          transitionPlanDate: null,
          needs: '',
          meetingDate: '',
          repParticipate: '',
          participatedNames: '',
          notify: '',
          consentShare: false,
          sectionDetermine: false,
          specify: '',
          makeReferral: false,
          date: '',
          tpc: false,
          leaInvited: '',
          noReferralMade: false,
        },
        transitionPreparation: {
          transitionPreparationDate: null,
          changesComingUp: '',
          preparationHelp: '',
          supports: '',
          communityOpportunities: '',
          serviceChangesNeeded: '',
        },
        consentForChangeForSDP: [
          {
            changes: '',
            isConsentSkipped: false,
            isAdminAmendment: false,
            isEligibleUntilThirdBirthday: '',
            eligibleTool: { id: '', label: '' },
            isEligibleFor: '',
            assessmentCompletionDate: null,
            reEstablishedDate: null,
            parentInitial: { id: '', label: '' },
            consentType: 'consent',
            sessionReviewedDate: '',
            participated: false,
            agreeDisagree: '',
            exceptionList: '',
            employeeSignature: {
              id: '',
              isUpdated: false,
              signature: '',
              verbalConsent: false,
              date: null,
              partitionKey: '',
              rowKey: '',
            },
            parentSignature: [
              {
                id: 'p.0',
                isUpdated: false,
                signature: '',
                verbalConsent: false,
                date: null,
                partitionKey: '',
                rowKey: '',
              },
              {
                id: 'p.1',
                isUpdated: false,
                signature: '',
                verbalConsent: false,
                date: null,
                partitionKey: '',
                rowKey: '',
              },
            ],
            consent: false,
            consentSignature: [
              {
                id: 'c.0',
                isUpdated: false,
                signature: '',
                verbalConsent: false,
                date: null,
                partitionKey: '',
                rowKey: '',
              },
              {
                id: 'c.1',
                isUpdated: false,
                signature: '',
                verbalConsent: false,
                date: null,
                partitionKey: '',
                rowKey: '',
              },
            ],
          },
        ],
      },
    },
    id: null,
    completedByRole: '',
    completedBy: { id: '', label: '' },
    primarySettingCode: '',
    primarySettingCodeJustification: '',
    ifspMeetingDate: null,
    ifspSignedDate: null,
    ifspDueDate: null,
    isLocked: false,
    lockWithoutConsentSignature: false,
    isReportUpdated: false,
    evaluationDate: null,
    notificationDate: null,
    initialEvalNotificationDate: null,
    referralBeginDate: null,
    dischargeDate: null,
    referralContactDate: null,
    selectedLanguage: '',
    isEIServiceOrOutcomesInfoDeleted: false,
  },
  initialIfsp: {
    programDetails: {
      name: '',
      imageUrl: '',
      email: '',
      phoneNumber: '',
      faxNumber: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      website: '',
    },
    client: {
      name: '',
      sex: '',
      dphId: '',
      dob: '',
    },
    reasonForDelay: {
      id: '',
      label: '',
    },
    reviewedList: [],
    ifspTeam: {
      contact: [],
      parents: [],
      teamMember: [],
      others: [],
      forms: {
        dailyRoutines: [
          {
            routines: initialRoutine,
            customRoutines: [],
            easiestTimes: [
              { routineName: '', reason: '' },
              { routineName: '', reason: '' },
              { routineName: '', reason: '' },
            ],
            hardestTimes: [
              { routineName: '', reason: '' },
              { routineName: '', reason: '' },
              { routineName: '', reason: '' },
            ],
            sessionReviewedDate: '',
          },
        ],
        healthSummary: [
          {
            dateOfEvaluation: null,
            teamMember: [],
            evaluationResults: '',
            healthHistory: '',
            currentHealthStatus: '',
            additionalInfo: '',
            sessionReviewedDate: '',
          },
        ],
        childIfspOutcomes: [
          {
            outcomesProgressReview: {
              outcomeId: '',
              startDate: null,
              endDate: null,
              concern: '',
              outcome: '',
              measurableProcedure: '',
              targetDate: '',
              isRecordSubmitted: false,
              tbrOutcomeId: 0,
              tbrUpdateRequired: true,
            },
            outcomesProgressInformation: [
              {
                date: null,
                progressDesc: '',
                progressRating: '',
              },
            ],
          },
        ],
        familyConcerns: [
          {
            concern: '',
            priorities: '',
            materialResource: '',
            infoResource: '',
            supportiveResource: '',
            sessionReviewedDate: '',
          },
        ],
        developmentalSummary: [
          {
            developmentalSummaryProgressReview: [
              {
                adaptive: '',
                cognition: '',
                communication: '',
                motor: '',
                personalSocial: '',
              },
            ],
            sessionReviewedDate: '',
          },
        ],
        serviceDeliveryPlan: [
          {
            earlyInterventionService: [],
            notes: '',
            justification: '',
            otherNeededService: '',
            stepsToTake: '',
          },
        ],
        transitionPlan: {
          consentDate: null,
          isTransitionPlanDone: '',
          isTpcMeetingDone: '',
          isConsentDoneToShareInfoToLea: '',
          leaEligible: '',
          noTPCReason: '',
          noTransitionPlanReason: '',
          outsideTPCReason: '',
          seaStatus: '',
          leaOptOutDate: null,
          transitionPlanDate: null,
          needs: '',
          meetingDate: '',
          repParticipate: '',
          participatedNames: '',
          notify: '',
          consentShare: false,
          sectionDetermine: false,
          specify: '',
          makeReferral: false,
          date: '',
          tpc: false,
          leaInvited: '',
          noReferralMade: false,
        },
        transitionPreparation: {
          transitionPreparationDate: null,
          changesComingUp: '',
          preparationHelp: '',
          supports: '',
          communityOpportunities: '',
          serviceChangesNeeded: '',
        },
        consentForChangeForSDP: [
          {
            changes: '',
            isConsentSkipped: false,
            isAdminAmendment: false,
            isEligibleUntilThirdBirthday: '',
            eligibleTool: { id: '', label: '' },
            isEligibleFor: '',
            assessmentCompletionDate: null,
            reEstablishedDate: null,
            parentInitial: { id: '', label: '' },
            consentType: 'consent',
            sessionReviewedDate: '',
            participated: false,
            agreeDisagree: '',
            exceptionList: '',
            employeeSignature: {
              id: '',
              isUpdated: false,
              signature: '',
              verbalConsent: false,
              date: null,
              partitionKey: '',
              rowKey: '',
            },
            parentSignature: [
              {
                id: 'p.0',
                isUpdated: false,
                signature: '',
                verbalConsent: false,
                date: null,
                partitionKey: '',
                rowKey: '',
              },
              {
                id: 'p.1',
                isUpdated: false,
                signature: '',
                verbalConsent: false,
                date: null,
                partitionKey: '',
                rowKey: '',
              },
            ],
            consent: false,
            consentSignature: [
              {
                id: 'c.0',
                isUpdated: false,
                signature: '',
                verbalConsent: false,
                date: null,
                partitionKey: '',
                rowKey: '',
              },
              {
                id: 'c.1',
                isUpdated: false,
                signature: '',
                verbalConsent: false,
                date: null,
                partitionKey: '',
                rowKey: '',
              },
            ],
          },
        ],
      },
    },
    id: null,
    completedByRole: '',
    completedBy: { id: '', label: '' },
    primarySettingCode: '',
    primarySettingCodeJustification: '',
    ifspMeetingDate: null,
    ifspSignedDate: null,
    ifspDueDate: null,
    isLocked: false,
    lockWithoutConsentSignature: false,
    isReportUpdated: false,
    evaluationDate: null,
    notificationDate: null,
    dischargeDate: null,
    initialEvalNotificationDate: null,
    referralBeginDate: null,
    referralContactDate: null,
    selectedLanguage: '',
    isEIServiceOrOutcomesInfoDeleted: false,
  },
  initialIfspForReports: {
    programDetails: {
      name: '',
      imageUrl: '',
      email: '',
      phoneNumber: '',
      faxNumber: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      website: '',
    },
    client: {
      name: '',
      sex: '',
      dphId: '',
      dob: '',
    },
    reasonForDelay: {
      id: '',
      label: '',
    },
    reviewedList: [],
    ifspTeam: {
      contact: [],
      parents: [],
      teamMember: [],
      others: [],
      forms: {
        dailyRoutines: [
          {
            routines: initialRoutine,
            customRoutines: [],
            easiestTimes: [
              { routineName: '', reason: '' },
              { routineName: '', reason: '' },
              { routineName: '', reason: '' },
            ],
            hardestTimes: [
              { routineName: '', reason: '' },
              { routineName: '', reason: '' },
              { routineName: '', reason: '' },
            ],
            sessionReviewedDate: '',
          },
        ],
        healthSummary: [
          {
            dateOfEvaluation: null,
            teamMember: [],
            evaluationResults: '',
            healthHistory: '',
            currentHealthStatus: '',
            additionalInfo: '',
            sessionReviewedDate: '',
          },
        ],
        childIfspOutcomes: [
          {
            outcomesProgressReview: {
              outcomeId: '',
              startDate: null,
              endDate: null,
              concern: '',
              outcome: '',
              measurableProcedure: '',
              targetDate: '',
              isRecordSubmitted: false,
              tbrOutcomeId: 0,
              tbrUpdateRequired: true,
            },
            outcomesProgressInformation: [
              {
                date: null,
                progressDesc: '',
                progressRating: '',
              },
            ],
          },
        ],
        familyConcerns: [
          {
            concern: '',
            priorities: '',
            materialResource: '',
            infoResource: '',
            supportiveResource: '',
            sessionReviewedDate: '',
          },
        ],
        developmentalSummary: [
          {
            developmentalSummaryProgressReview: [
              {
                adaptive: '',
                cognition: '',
                communication: '',
                motor: '',
                personalSocial: '',
              },
            ],
            sessionReviewedDate: '',
          },
        ],
        serviceDeliveryPlan: [
          {
            earlyInterventionService: [],
            notes: '',
            justification: '',
            otherNeededService: '',
            stepsToTake: '',
          },
        ],
        transitionPlan: {
          consentDate: null,
          isTransitionPlanDone: '',
          isTpcMeetingDone: '',
          isConsentDoneToShareInfoToLea: '',
          leaEligible: '',
          noTPCReason: '',
          noTransitionPlanReason: '',
          outsideTPCReason: '',
          seaStatus: '',
          leaOptOutDate: null,
          transitionPlanDate: null,
          needs: '',
          meetingDate: '',
          repParticipate: '',
          participatedNames: '',
          notify: '',
          consentShare: false,
          sectionDetermine: false,
          specify: '',
          makeReferral: false,
          date: '',
          tpc: false,
          leaInvited: '',
          noReferralMade: false,
        },
        transitionPreparation: {
          transitionPreparationDate: null,
          changesComingUp: '',
          preparationHelp: '',
          supports: '',
          communityOpportunities: '',
          serviceChangesNeeded: '',
        },
        consentForChangeForSDP: [
          {
            changes: '',
            isConsentSkipped: false,
            isAdminAmendment: false,
            isEligibleUntilThirdBirthday: '',
            eligibleTool: { id: '', label: '' },
            isEligibleFor: '',
            assessmentCompletionDate: null,
            reEstablishedDate: null,
            parentInitial: { id: '', label: '' },
            consentType: 'consent',
            sessionReviewedDate: '',
            participated: false,
            agreeDisagree: '',
            exceptionList: '',
            employeeSignature: {
              id: '',
              isUpdated: false,
              signature: '',
              verbalConsent: false,
              date: null,
              partitionKey: '',
              rowKey: '',
            },
            parentSignature: [
              {
                id: 'p.0',
                isUpdated: false,
                signature: '',
                verbalConsent: false,
                date: null,
                partitionKey: '',
                rowKey: '',
              },
              {
                id: 'p.1',
                isUpdated: false,
                signature: '',
                verbalConsent: false,
                date: null,
                partitionKey: '',
                rowKey: '',
              },
            ],
            consent: false,
            consentSignature: [
              {
                id: 'c.0',
                isUpdated: false,
                signature: '',
                verbalConsent: false,
                date: null,
                partitionKey: '',
                rowKey: '',
              },
              {
                id: 'c.1',
                isUpdated: false,
                signature: '',
                verbalConsent: false,
                date: null,
                partitionKey: '',
                rowKey: '',
              },
            ],
          },
        ],
      },
    },
    id: null,
    completedByRole: '',
    completedBy: { id: '', label: '' },
    primarySettingCode: '',
    primarySettingCodeJustification: '',
    ifspMeetingDate: null,
    ifspSignedDate: null,
    ifspDueDate: null,
    isLocked: false,
    lockWithoutConsentSignature: false,
    isReportUpdated: false,
    evaluationDate: null,
    notificationDate: null,
    dischargeDate: null,
    initialEvalNotificationDate: null,
    referralBeginDate: null,
    referralContactDate: null,
    selectedLanguage: '',
    isEIServiceOrOutcomesInfoDeleted: false,
  },
  employeeData: [
    {
      id: '',
      name: '',
      firstName: '',
      lastName: '',
      email: '',
      disciplineType: '',
      phone: '',
      roles: [],
    },
  ],
  updateIfspContext: () => {},
  updateInitialIfspContext: () => {},
  updateIfspForReport: () => {},
  updateIfspTouched: () => {},
  UpdateLeaReferralDateOutOfRange: () => {},
  UpdateTpcMeetingDateOutOfRange: () => {},
  updateTransitionOutOfRange: () => {},
  updateIsOptOutNotificationDateRequired: () => {},
  updateIsTransitionDateReqired: () => {},
  updateIsLeaReferralDateRequired: () => {},
  updateIsTpcMeetingDateRequired: () => {},
  updateOutComesFieldFlag: () => {},
  updateOutComesStartDateFlag: () => {},
  updateOutComesEndDateFlag: () => {},
  getIfsp: () => {},
  ifspTouched: false,
  isTransitionDateReqired: false,
  isLeaReferralDateRequired: false,
  isTpcMeetingDateRequired: false,
  isOptOutNotificationDateRequired: false,
  leaReferralDateOutOfRange: false,
  tpcMeetingDateOutOfRange: false,
  transitionDateOutOfRange: false,
  primarySettingCode: [],
  reasonData: [],
  activeReasonData: [],
  isDailyRoutineUpdated: false,
  isDevelopmentSummaryUpdated: false,
  isFamilyConcernsUpdated: false,
  isOutComeEndDateHasIssue: false,
  isOutComeStartDateHasIssue: false,
  isHealthAndEligibilityUpdated: false,
  isAllOutComesUpdated: false,
});

export const IfspContextProvider = ({ children }: IfspContextType) => {
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const { lookups } = useContext(LookUpContext) as LookUpContextType;

  const getEmployeeData = (employeeIds: string[]) => {
    getAllEmployeeMinimalList(employeeIds)
      .then((data) => {
        setEmployeeData(data);
      })
      .catch((error) => {
        toggleLoader(false);
        setOpen(true);
        setToastrVariable('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('IFSP.failedToGetEmployeeData');
          setToastrDefaultMessage('Failed to get employee data');
        }
      });
  };
  const getIfsp = (clientId: string) => {
    if (clientId) {
      getIfspById(clientId)
        .then((data: IfspContextDataType) => {
          setIsOptOutNotificationDateRequired(
            (data.ifspTeam.forms.transitionPlan.leaOptOutDate === null ||
              data.ifspTeam.forms.transitionPlan.leaOptOutDate === '') &&
              data.ifspTeam.forms.transitionPlan.seaStatus === 'OPTOUT',
          );

          const teamMemberIds = data.ifspTeam.teamMember
            .filter((x) => x.role)
            .map((x) => x.id);
          const healthSummaryEmployeeIds =
            data.ifspTeam.forms.healthSummary.flatMap((x) =>
              x.teamMember.map((y) => y.name),
            );
          const reportCompletedById = data.completedBy.id;
          const allEmployeeIds = [
            ...teamMemberIds,
            ...healthSummaryEmployeeIds.flat(),
            reportCompletedById,
          ];
          const uniqueEmpIds = [...new Set(allEmployeeIds)];
          console.log(
            'allEmployeeIds',
            allEmployeeIds,
            'teamMemberIds',
            teamMemberIds,
            'healthSummaryEmployeeIds',
            healthSummaryEmployeeIds,
            'uniqueEmpIds',
            uniqueEmpIds,
          );
          getEmployeeData(uniqueEmpIds);
          setIfsp(
            cloneDeep({
              ...data,
              ifspTeam: {
                ...data.ifspTeam,
                forms: {
                  ...data.ifspTeam.forms,
                  dailyRoutines: data.ifspTeam.forms.dailyRoutines.map((x) => ({
                    ...x,
                    easiestTimes:
                      x.easiestTimes.length === 3
                        ? x.easiestTimes
                        : [
                            { routineName: '', reason: '' },
                            { routineName: '', reason: '' },
                            { routineName: '', reason: '' },
                          ],
                    hardestTimes:
                      x.hardestTimes.length === 3
                        ? x.hardestTimes
                        : [
                            { routineName: '', reason: '' },
                            { routineName: '', reason: '' },
                            { routineName: '', reason: '' },
                          ],
                  })),
                  familyConcerns: data.ifspTeam.forms.familyConcerns.map(
                    (x) => ({
                      concern: x.concern,
                      infoResource: x.infoResource,
                      materialResource: x.materialResource,
                      priorities: x.priorities,
                      sessionReviewedDate: x.sessionReviewedDate,
                      supportiveResource: x.supportiveResource,
                    }),
                  ),
                  childIfspOutcomes: data.ifspTeam.forms.childIfspOutcomes.map(
                    (x) => ({
                      ...x,
                      outcomesProgressReview: {
                        ...x.outcomesProgressReview,
                        endDate:
                          x.outcomesProgressReview.endDate === ''
                            ? null
                            : x.outcomesProgressReview.endDate,
                      },
                    }),
                  ),
                  developmentalSummary:
                    data.ifspTeam.forms.developmentalSummary.map((x) => ({
                      ...x,
                      developmentalSummaryProgressReview:
                        x.developmentalSummaryProgressReview.length === 0
                          ? [
                              {
                                adaptive: '',
                                cognition: '',
                                motivation: '',
                                communication: '',
                                motor: '',
                                personalSocial: '',
                              },
                            ]
                          : x.developmentalSummaryProgressReview,
                    })),
                  consentForChangeForSDP:
                    data.ifspTeam.forms.consentForChangeForSDP.map(
                      (x, index) => ({
                        ...x,
                        consentType:
                          x.consentType === ''
                            ? index === 0
                              ? 'consent'
                              : 'consentForChange'
                            : x.consentType,
                        isConsentSkipped: x.isConsentSkipped ?? false,
                        sessionReviewedDate:
                          ((!x.isConsentSkipped &&
                            x.parentSignature.every(
                              (sig) =>
                                !sig.signature &&
                                !sig.rowKey &&
                                !sig.verbalConsent,
                            )) ||
                            (!x.isConsentSkipped &&
                              x.consentSignature.every(
                                (sig) =>
                                  !sig.signature &&
                                  !sig.rowKey &&
                                  !sig.verbalConsent,
                              ))) &&
                          !data.lockWithoutConsentSignature
                            ? ''
                            : x.sessionReviewedDate,
                        consentSignature: x.consentSignature.map(
                          (y, yIndex) => ({
                            ...y,
                            id: y.id ? y.id : `c.${yIndex}`,
                            date: y.date,
                          }),
                        ),
                        parentSignature: x.parentSignature.map((y, yIndex) => ({
                          ...y,
                          id: y.id ? y.id : `p.${yIndex}`,
                          date: y.date,
                        })),
                      }),
                    ),
                },
              },
              programDetails: {
                ...data.programDetails,
                imageUrl:
                  data.programDetails.imageUrl &&
                  (data.programDetails.imageUrl !== null ||
                    data.programDetails.imageUrl !== '')
                    ? data.programDetails.imageUrl
                    : ThomProgram,
              },
              id: data.id,
              ifspMeetingDate: data.ifspMeetingDate || null,
              ifspSignedDate: data.ifspSignedDate || null,
              ifspDueDate: data.ifspDueDate,
              isReportUpdated: false,
            }),
          );
          setInitialIfsp(
            cloneDeep({
              ...data,
              ifspTeam: {
                ...data.ifspTeam,
                forms: {
                  ...data.ifspTeam.forms,
                  familyConcerns: data.ifspTeam.forms.familyConcerns.map(
                    (x) => ({
                      concern: x.concern,
                      infoResource: x.infoResource,
                      materialResource: x.materialResource,
                      priorities: x.priorities,
                      sessionReviewedDate: x.sessionReviewedDate,
                      supportiveResource: x.supportiveResource,
                    }),
                  ),
                  dailyRoutines: data.ifspTeam.forms.dailyRoutines.map((x) => ({
                    ...x,
                    easiestTimes:
                      x.easiestTimes.length === 3
                        ? x.easiestTimes
                        : [
                            { routineName: '', reason: '' },
                            { routineName: '', reason: '' },
                            { routineName: '', reason: '' },
                          ],
                    hardestTimes:
                      x.hardestTimes.length === 3
                        ? x.hardestTimes
                        : [
                            { routineName: '', reason: '' },
                            { routineName: '', reason: '' },
                            { routineName: '', reason: '' },
                          ],
                  })),
                  childIfspOutcomes: data.ifspTeam.forms.childIfspOutcomes.map(
                    (x) => ({
                      ...x,
                      outcomesProgressReview: {
                        ...x.outcomesProgressReview,
                        endDate:
                          x.outcomesProgressReview.endDate === ''
                            ? null
                            : x.outcomesProgressReview.endDate,
                      },
                    }),
                  ),
                  developmentalSummary:
                    data.ifspTeam.forms.developmentalSummary.map((x) => ({
                      ...x,
                      developmentalSummaryProgressReview:
                        x.developmentalSummaryProgressReview.length === 0
                          ? [
                              {
                                adaptive: '',
                                cognition: '',
                                motivation: '',
                                communication: '',
                                motor: '',
                                personalSocial: '',
                              },
                            ]
                          : x.developmentalSummaryProgressReview,
                    })),
                  consentForChangeForSDP:
                    data.ifspTeam.forms.consentForChangeForSDP.map(
                      (x, index) => ({
                        ...x,
                        consentType:
                          x.consentType === ''
                            ? index === 0
                              ? 'consent'
                              : 'consentForChange'
                            : x.consentType,
                        isConsentSkipped: x.isConsentSkipped ?? false,
                        sessionReviewedDate:
                          ((!x.isConsentSkipped &&
                            x.parentSignature.every(
                              (sig) =>
                                !sig.signature &&
                                !sig.rowKey &&
                                !sig.verbalConsent,
                            )) ||
                            (!x.isConsentSkipped &&
                              x.consentSignature.every(
                                (sig) =>
                                  !sig.signature &&
                                  !sig.rowKey &&
                                  !sig.verbalConsent,
                              ))) &&
                          !data.lockWithoutConsentSignature
                            ? ''
                            : x.sessionReviewedDate,
                        consentSignature: x.consentSignature.map(
                          (y, yIndex) => ({
                            ...y,
                            id: y.id ? y.id : `c.${yIndex}`,
                            date: y.date,
                          }),
                        ),
                        parentSignature: x.parentSignature.map((y, yIndex) => ({
                          ...y,
                          date: y.date,
                          id: y.id ? y.id : `p.${yIndex}`,
                        })),
                      }),
                    ),
                },
              },
              programDetails: {
                ...data.programDetails,
                imageUrl:
                  data.programDetails.imageUrl &&
                  (data.programDetails.imageUrl !== null ||
                    data.programDetails.imageUrl !== '')
                    ? data.programDetails.imageUrl
                    : ThomProgram,
              },
              id: data.id,
              ifspMeetingDate: data.ifspMeetingDate || null,
              ifspSignedDate: data.ifspSignedDate || null,
              ifspDueDate: data.ifspDueDate,
              isReportUpdated: false,
            }),
          );
          setInitialIfspForReports(
            cloneDeep({
              ...data,
              ifspTeam: {
                ...data.ifspTeam,
                forms: {
                  ...data.ifspTeam.forms,
                  dailyRoutines: data.ifspTeam.forms.dailyRoutines.map((x) => ({
                    ...x,
                    easiestTimes:
                      x.easiestTimes.length === 3
                        ? x.easiestTimes
                        : [
                            { routineName: '', reason: '' },
                            { routineName: '', reason: '' },
                            { routineName: '', reason: '' },
                          ],
                    hardestTimes:
                      x.hardestTimes.length === 3
                        ? x.hardestTimes
                        : [
                            { routineName: '', reason: '' },
                            { routineName: '', reason: '' },
                            { routineName: '', reason: '' },
                          ],
                  })),
                  familyConcerns: data.ifspTeam.forms.familyConcerns.map(
                    (x) => ({
                      concern: x.concern,
                      infoResource: x.infoResource,
                      materialResource: x.materialResource,
                      priorities: x.priorities,
                      sessionReviewedDate: x.sessionReviewedDate,
                      supportiveResource: x.supportiveResource,
                    }),
                  ),
                  childIfspOutcomes: data.ifspTeam.forms.childIfspOutcomes.map(
                    (x) => ({
                      ...x,
                      outcomesProgressReview: {
                        ...x.outcomesProgressReview,
                        endDate:
                          x.outcomesProgressReview.endDate === ''
                            ? null
                            : x.outcomesProgressReview.endDate,
                      },
                    }),
                  ),
                  developmentalSummary:
                    data.ifspTeam.forms.developmentalSummary.map((x) => ({
                      ...x,
                      developmentalSummaryProgressReview:
                        x.developmentalSummaryProgressReview.length === 0
                          ? [
                              {
                                adaptive: '',
                                cognition: '',
                                motivation: '',
                                communication: '',
                                motor: '',
                                personalSocial: '',
                              },
                            ]
                          : x.developmentalSummaryProgressReview,
                    })),
                  consentForChangeForSDP:
                    data.ifspTeam.forms.consentForChangeForSDP.map(
                      (x, index) => ({
                        ...x,
                        consentType:
                          x.consentType === ''
                            ? index === 0
                              ? 'consent'
                              : 'consentForChange'
                            : x.consentType,
                        isConsentSkipped: x.isConsentSkipped ?? false,
                        sessionReviewedDate:
                          ((!x.isConsentSkipped &&
                            x.parentSignature.every(
                              (sig) =>
                                !sig.signature &&
                                !sig.rowKey &&
                                !sig.verbalConsent,
                            )) ||
                            (!x.isConsentSkipped &&
                              x.consentSignature.every(
                                (sig) =>
                                  !sig.signature &&
                                  !sig.rowKey &&
                                  !sig.verbalConsent,
                              ))) &&
                          !data.lockWithoutConsentSignature
                            ? ''
                            : x.sessionReviewedDate,
                        consentSignature: x.consentSignature.map(
                          (y, yIndex) => ({
                            ...y,
                            date: y.date,
                            id: y.id ? y.id : `c.${yIndex}`,
                          }),
                        ),
                        parentSignature: x.parentSignature.map((y, yIndex) => ({
                          ...y,
                          date: y.date,
                          id: y.id ? y.id : `p.${yIndex}`,
                        })),
                      }),
                    ),
                },
              },
              programDetails: {
                ...data.programDetails,
                imageUrl:
                  data.programDetails.imageUrl &&
                  (data.programDetails.imageUrl !== null ||
                    data.programDetails.imageUrl !== '')
                    ? data.programDetails.imageUrl
                    : ThomProgram,
              },
              id: data.id,
              ifspMeetingDate: data.ifspMeetingDate || null,
              ifspSignedDate: data.ifspSignedDate || null,
              ifspDueDate: data.ifspDueDate,
              isReportUpdated: false,
            }),
          );
        })
        .catch((error) => {
          toggleLoader(false);
          setOpen(true);
          setToastrVariable('error');
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId('IFSP.failedToGet');
            setToastrDefaultMessage('Failed to get signature');
          }
        });
    }
  };
  useEffect(() => {
    toggleLoader(true);
    const clientId = localStorage.getItem('ClientId');
    if (clientId) {
      getIfsp(clientId);
    }
  }, []);

  const [ifsp, setIfsp] = useState<IfspContextDataType>(
    cloneDeep({
      programDetails: {
        name: '',
        imageUrl: '',
        email: '',
        phoneNumber: '',
        faxNumber: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
        website: '',
      },
      client: {
        name: '',
        sex: '',
        dphId: '',
        dob: '',
      },
      reasonForDelay: {
        id: '',
        label: '',
      },
      reviewedList: [],
      ifspTeam: {
        contact: [],
        parents: [],
        teamMember: [],
        others: [],
        forms: {
          dailyRoutines: [
            {
              routines: initialRoutine,
              customRoutines: [],
              easiestTimes: [
                { routineName: '', reason: '' },
                { routineName: '', reason: '' },
                { routineName: '', reason: '' },
              ],
              hardestTimes: [
                { routineName: '', reason: '' },
                { routineName: '', reason: '' },
                { routineName: '', reason: '' },
              ],
              sessionReviewedDate: '',
            },
          ],
          healthSummary: [
            {
              dateOfEvaluation: null,
              teamMember: [],
              evaluationResults: '',
              healthHistory: '',
              currentHealthStatus: '',
              additionalInfo: '',
              sessionReviewedDate: '',
            },
          ],
          childIfspOutcomes: [
            {
              outcomesProgressReview: {
                outcomeId: '',
                startDate: null,
                endDate: null,
                concern: '',
                outcome: '',
                measurableProcedure: '',
                targetDate: '',
                reason: '',
                isRecordSubmitted: false,
                tbrOutcomeId: 0,
                tbrUpdateRequired: true,
              },
              outcomesProgressInformation: [
                {
                  date: null,
                  progressDesc: '',
                  progressRating: '',
                },
              ],
            },
          ],
          familyConcerns: [
            {
              concern: '',
              priorities: '',
              materialResource: '',
              infoResource: '',
              supportiveResource: '',
              sessionReviewedDate: '',
            },
          ],
          developmentalSummary: [
            {
              developmentalSummaryProgressReview: [
                {
                  adaptive: '',
                  cognition: '',
                  communication: '',
                  motor: '',
                  personalSocial: '',
                },
              ],
              sessionReviewedDate: '',
            },
          ],
          serviceDeliveryPlan: [
            {
              earlyInterventionService: [],
              notes: '',
              justification: '',
              otherNeededService: '',
              stepsToTake: '',
            },
          ],
          transitionPlan: {
            consentDate: null,
            isTpcMeetingDone: '',
            isConsentDoneToShareInfoToLea: '',
            isTransitionPlanDone: '',
            leaEligible: '',
            noTPCReason: '',
            noTransitionPlanReason: '',
            outsideTPCReason: '',
            leaOptOutDate: null,
            seaStatus: '',
            transitionPlanDate: null,
            needs: '',
            meetingDate: '',
            repParticipate: '',
            participatedNames: '',
            notify: '',
            consentShare: false,
            sectionDetermine: false,
            specify: '',
            makeReferral: false,
            date: '',
            tpc: false,
            leaInvited: '',
            noReferralMade: false,
          },
          transitionPreparation: {
            transitionPreparationDate: null,
            changesComingUp: '',
            preparationHelp: '',
            supports: '',
            communityOpportunities: '',
            serviceChangesNeeded: '',
          },
          consentForChangeForSDP: [
            {
              changes: '',
              isConsentSkipped: false,
              isAdminAmendment: false,
              isEligibleUntilThirdBirthday: '',
              eligibleTool: { id: '', label: '' },
              isEligibleFor: '',
              assessmentCompletionDate: null,
              reEstablishedDate: null,
              parentInitial: { id: '', label: '' },
              consentType: 'consent',
              sessionReviewedDate: '',
              participated: false,
              agreeDisagree: '',
              exceptionList: '',
              employeeSignature: {
                id: '',
                isUpdated: false,
                signature: '',
                verbalConsent: false,
                date: null,
                partitionKey: '',
                rowKey: '',
              },
              parentSignature: [
                {
                  id: 'p.0',
                  isUpdated: false,
                  signature: '',
                  verbalConsent: false,
                  date: null,
                  partitionKey: '',
                  rowKey: '',
                },
                {
                  id: 'p.1',
                  isUpdated: false,
                  signature: '',
                  verbalConsent: false,
                  date: null,
                  partitionKey: '',
                  rowKey: '',
                },
              ],
              consent: false,
              consentSignature: [
                {
                  id: 'c.0',
                  isUpdated: false,
                  signature: '',
                  verbalConsent: false,
                  date: null,
                  partitionKey: '',
                  rowKey: '',
                },
                {
                  id: 'c.1',
                  isUpdated: false,
                  signature: '',
                  verbalConsent: false,
                  date: null,
                  partitionKey: '',
                  rowKey: '',
                },
              ],
            },
          ],
        },
      },
      id: null,
      completedByRole: '',
      completedBy: { id: '', label: '' },
      primarySettingCode: '',
      primarySettingCodeJustification: '',
      ifspMeetingDate: null,
      ifspSignedDate: null,
      ifspDueDate: null,
      isLocked: false,
      lockWithoutConsentSignature: false,
      isReportUpdated: false,
      evaluationDate: null,
      notificationDate: null,
      dischargeDate: null,
      initialEvalNotificationDate: null,
      referralBeginDate: null,
      referralContactDate: null,
      selectedLanguage: '',
      isEIServiceOrOutcomesInfoDeleted: false,
    }),
  );

  const [open, setOpen] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const [initialIfsp, setInitialIfsp] = useState<IfspContextDataType>(
    cloneDeep({
      programDetails: {
        name: '',
        imageUrl: '',
        email: '',
        phoneNumber: '',
        faxNumber: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
        website: '',
      },
      client: {
        name: '',
        sex: '',
        dphId: '',
        dob: '',
      },
      reasonForDelay: {
        id: '',
        label: '',
      },
      reviewedList: [],
      ifspTeam: {
        contact: [],
        parents: [],
        teamMember: [],
        others: [],
        forms: {
          dailyRoutines: [
            {
              routines: initialRoutine,
              customRoutines: [],
              easiestTimes: [
                { routineName: '', reason: '' },
                { routineName: '', reason: '' },
                { routineName: '', reason: '' },
              ],
              hardestTimes: [
                { routineName: '', reason: '' },
                { routineName: '', reason: '' },
                { routineName: '', reason: '' },
              ],
              sessionReviewedDate: '',
            },
          ],
          healthSummary: [
            {
              dateOfEvaluation: null,
              teamMember: [],
              evaluationResults: '',
              healthHistory: '',
              currentHealthStatus: '',
              additionalInfo: '',
              sessionReviewedDate: '',
            },
          ],
          childIfspOutcomes: [
            {
              outcomesProgressReview: {
                outcomeId: '',
                startDate: null,
                endDate: null,
                concern: '',
                outcome: '',
                measurableProcedure: '',
                targetDate: '',
                reason: '',
                isRecordSubmitted: false,
                tbrOutcomeId: 0,
                tbrUpdateRequired: true,
              },
              outcomesProgressInformation: [
                {
                  date: null,
                  progressDesc: '',
                  progressRating: '',
                },
              ],
            },
          ],
          familyConcerns: [
            {
              concern: '',
              priorities: '',
              materialResource: '',
              infoResource: '',
              supportiveResource: '',
              sessionReviewedDate: '',
            },
          ],
          developmentalSummary: [
            {
              developmentalSummaryProgressReview: [
                {
                  adaptive: '',
                  cognition: '',
                  communication: '',
                  motor: '',
                  personalSocial: '',
                },
              ],
              sessionReviewedDate: '',
            },
          ],
          serviceDeliveryPlan: [
            {
              earlyInterventionService: [],
              notes: '',
              justification: '',
              otherNeededService: '',
              stepsToTake: '',
            },
          ],
          transitionPlan: {
            consentDate: null,
            isTpcMeetingDone: '',
            isConsentDoneToShareInfoToLea: '',
            isTransitionPlanDone: '',
            leaEligible: '',
            noTPCReason: '',
            noTransitionPlanReason: '',
            outsideTPCReason: '',
            seaStatus: '',
            leaOptOutDate: null,
            transitionPlanDate: null,
            needs: '',
            meetingDate: '',
            repParticipate: '',
            participatedNames: '',
            notify: '',
            consentShare: false,
            sectionDetermine: false,
            specify: '',
            makeReferral: false,
            date: '',
            tpc: false,
            leaInvited: '',
            noReferralMade: false,
          },
          transitionPreparation: {
            transitionPreparationDate: null,
            changesComingUp: '',
            preparationHelp: '',
            supports: '',
            communityOpportunities: '',
            serviceChangesNeeded: '',
          },
          consentForChangeForSDP: [
            {
              changes: '',
              isConsentSkipped: false,
              isAdminAmendment: false,
              isEligibleUntilThirdBirthday: '',
              eligibleTool: { id: '', label: '' },
              isEligibleFor: '',
              assessmentCompletionDate: null,
              reEstablishedDate: null,
              parentInitial: { id: '', label: '' },
              consentType: 'consent',
              sessionReviewedDate: '',
              participated: false,
              agreeDisagree: '',
              exceptionList: '',
              employeeSignature: {
                id: '',
                isUpdated: false,
                signature: '',
                verbalConsent: false,
                date: null,
                partitionKey: '',
                rowKey: '',
              },
              parentSignature: [
                {
                  id: 'p.0',
                  isUpdated: false,
                  signature: '',
                  verbalConsent: false,
                  date: null,
                  partitionKey: '',
                  rowKey: '',
                },
                {
                  id: 'p.1',
                  isUpdated: false,
                  signature: '',
                  verbalConsent: false,
                  date: null,
                  partitionKey: '',
                  rowKey: '',
                },
              ],
              consent: false,
              consentSignature: [
                {
                  id: 'c.0',
                  isUpdated: false,
                  signature: '',
                  verbalConsent: false,
                  date: null,
                  partitionKey: '',
                  rowKey: '',
                },
                {
                  id: 'c.1',
                  isUpdated: false,
                  signature: '',
                  verbalConsent: false,
                  date: null,
                  partitionKey: '',
                  rowKey: '',
                },
              ],
            },
          ],
        },
      },
      id: null,
      completedByRole: '',
      completedBy: { id: '', label: '' },
      primarySettingCode: '',
      primarySettingCodeJustification: '',
      ifspMeetingDate: null,
      ifspSignedDate: null,
      ifspDueDate: null,
      isLocked: false,
      lockWithoutConsentSignature: false,
      isReportUpdated: false,
      evaluationDate: null,
      notificationDate: null,
      dischargeDate: null,
      initialEvalNotificationDate: null,
      referralBeginDate: null,
      referralContactDate: null,
      selectedLanguage: '',
      isEIServiceOrOutcomesInfoDeleted: false,
    }),
  );
  const [initialIfspForReports, setInitialIfspForReports] =
    useState<IfspContextDataType>(
      cloneDeep({
        programDetails: {
          name: '',
          imageUrl: '',
          email: '',
          phoneNumber: '',
          faxNumber: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          zipCode: '',
          website: '',
        },
        client: {
          name: '',
          sex: '',
          dphId: '',
          dob: '',
        },
        reasonForDelay: {
          id: '',
          label: '',
        },
        reviewedList: [],
        ifspTeam: {
          contact: [],
          parents: [],
          teamMember: [],
          others: [],
          forms: {
            dailyRoutines: [
              {
                routines: initialRoutine,
                customRoutines: [],
                easiestTimes: [
                  { routineName: '', reason: '' },
                  { routineName: '', reason: '' },
                  { routineName: '', reason: '' },
                ],
                hardestTimes: [
                  { routineName: '', reason: '' },
                  { routineName: '', reason: '' },
                  { routineName: '', reason: '' },
                ],
                sessionReviewedDate: '',
              },
            ],
            healthSummary: [
              {
                dateOfEvaluation: null,
                teamMember: [],
                evaluationResults: '',
                healthHistory: '',
                currentHealthStatus: '',
                additionalInfo: '',
                sessionReviewedDate: '',
              },
            ],
            childIfspOutcomes: [
              {
                outcomesProgressReview: {
                  outcomeId: '',
                  startDate: null,
                  endDate: null,
                  concern: '',
                  outcome: '',
                  measurableProcedure: '',
                  targetDate: '',
                  reason: '',
                  isRecordSubmitted: false,
                  tbrOutcomeId: 0,
                  tbrUpdateRequired: true,
                },
                outcomesProgressInformation: [
                  {
                    date: null,
                    progressDesc: '',
                    progressRating: '',
                  },
                ],
              },
            ],
            familyConcerns: [
              {
                concern: '',
                priorities: '',
                materialResource: '',
                infoResource: '',
                supportiveResource: '',
                sessionReviewedDate: '',
              },
            ],
            developmentalSummary: [
              {
                developmentalSummaryProgressReview: [
                  {
                    adaptive: '',
                    cognition: '',
                    communication: '',
                    motor: '',
                    personalSocial: '',
                  },
                ],
                sessionReviewedDate: '',
              },
            ],
            serviceDeliveryPlan: [
              {
                earlyInterventionService: [],
                notes: '',
                justification: '',
                otherNeededService: '',
                stepsToTake: '',
              },
            ],
            transitionPlan: {
              consentDate: null,
              isTpcMeetingDone: '',
              isConsentDoneToShareInfoToLea: '',
              isTransitionPlanDone: '',
              leaEligible: '',
              noTPCReason: '',
              noTransitionPlanReason: '',
              outsideTPCReason: '',
              seaStatus: '',
              leaOptOutDate: null,
              transitionPlanDate: null,
              needs: '',
              meetingDate: '',
              repParticipate: '',
              participatedNames: '',
              notify: '',
              consentShare: false,
              sectionDetermine: false,
              specify: '',
              makeReferral: false,
              date: '',
              tpc: false,
              leaInvited: '',
              noReferralMade: false,
            },
            transitionPreparation: {
              transitionPreparationDate: null,
              changesComingUp: '',
              preparationHelp: '',
              supports: '',
              communityOpportunities: '',
              serviceChangesNeeded: '',
            },
            consentForChangeForSDP: [
              {
                changes: '',
                isConsentSkipped: false,
                isAdminAmendment: false,
                isEligibleUntilThirdBirthday: '',
                eligibleTool: { id: '', label: '' },
                isEligibleFor: '',
                assessmentCompletionDate: null,
                reEstablishedDate: null,
                parentInitial: { id: '', label: '' },
                consentType: 'consent',
                sessionReviewedDate: '',
                participated: false,
                agreeDisagree: '',
                exceptionList: '',
                employeeSignature: {
                  id: '',
                  isUpdated: false,
                  signature: '',
                  verbalConsent: false,
                  date: null,
                  partitionKey: '',
                  rowKey: '',
                },
                parentSignature: [
                  {
                    id: 'p.0',
                    isUpdated: false,
                    signature: '',
                    verbalConsent: false,
                    date: null,
                    partitionKey: '',
                    rowKey: '',
                  },
                  {
                    id: 'p.1',
                    isUpdated: false,
                    signature: '',
                    verbalConsent: false,
                    date: null,
                    partitionKey: '',
                    rowKey: '',
                  },
                ],
                consent: false,
                consentSignature: [
                  {
                    id: 'c.0',
                    isUpdated: false,
                    signature: '',
                    verbalConsent: false,
                    date: null,
                    partitionKey: '',
                    rowKey: '',
                  },
                  {
                    id: 'c.1',
                    isUpdated: false,
                    signature: '',
                    verbalConsent: false,
                    date: null,
                    partitionKey: '',
                    rowKey: '',
                  },
                ],
              },
            ],
          },
        },
        id: null,
        completedByRole: '',
        completedBy: { id: '', label: '' },
        primarySettingCode: '',
        primarySettingCodeJustification: '',
        ifspMeetingDate: null,
        ifspSignedDate: null,
        ifspDueDate: null,
        isLocked: false,
        lockWithoutConsentSignature: false,
        isReportUpdated: false,
        evaluationDate: null,
        notificationDate: null,
        dischargeDate: null,
        initialEvalNotificationDate: null,
        referralBeginDate: null,
        referralContactDate: null,
        selectedLanguage: '',
        isEIServiceOrOutcomesInfoDeleted: false,
      }),
    );
  const [ifspTouched, setIfspTouched] = useState<boolean>(false);
  const [isDailyRoutineUpdated, setIsDailyRoutineUpdated] =
    useState<boolean>(false);
  const [isFamilyConcernsUpdated, setIsFamilyConcernsUpdated] =
    useState<boolean>(false);
  const [isHealthAndEligibilityUpdated, setIsHealthAndEligibilityUpdated] =
    useState<boolean>(false);
  const [isDevelopmentSummaryUpdated, setIsDevelopmentSummaryUpdated] =
    useState<boolean>(false);
  const [isAllOutComesUpdated, setIsAllOutComesUpdated] =
    useState<boolean>(false);
  const [isOutComeStartDateHasIssue, setIsOutComeStartDateHasIssue] =
    useState<boolean>(false);
  const [isOutComeEndDateHasIssue, setIsOutComeEndDateHasIssue] =
    useState<boolean>(false);
  const [isTransitionDateReqired, setIsTransitionDateReqired] =
    useState<boolean>(false);
  const [isTpcMeetingDateRequired, setIsTpcMeetingDateRequired] =
    useState<boolean>(false);
  const [isLeaReferralDateRequired, setIsLeaReferralDateRequired] =
    useState<boolean>(false);
  const [
    isOptOutNotificationDateRequired,
    setIsOptOutNotificationDateRequired,
  ] = useState<boolean>(false);
  const [transitionDateOutOfRange, setTransitionDateOutOfRange] =
    useState<boolean>(false);
  const [leaReferralDateOutOfRange, setLeaReferralDateOutOfRange] =
    useState<boolean>(false);
  const [tpcMeetingDateOutOfRange, setTpcMeetingDateOutOfRange] =
    useState<boolean>(false);

  const [primarySettingCode, setPrimarySettingCode] = useState<OptionType[]>(
    [],
  );
  const [reasonData, setReasonData] = useState<OptionType[]>([]);
  const [activeReasonData, setActiveReasonData] = useState<OptionType[]>([]);

  const [employeeData, setEmployeeData] = useState<EmployeeListType[]>([
    {
      id: '',
      name: '',
      firstName: '',
      lastName: '',
      email: '',
      disciplineType: '',
      phone: '',
      roles: [],
    },
  ]);

  const { handleUpdateUnsavedChanges } = useContext(UnSavedChangesContext);

  useEffect(() => {
    if (lookups) {
      setPrimarySettingCode(() => {
        return lookups.primarySettingCode.map((item) => {
          return { id: item.code, label: item.description };
        });
      });
      setReasonData(() => {
        return lookups.reason.map((item) => {
          return { id: item.code, label: item.description };
        });
      });

      setActiveReasonData(() => {
        return lookups.reason
          .filter((item) => item.active || item.code === ifsp.reasonForDelay.id)
          .map((item) => {
            return { id: item.code, label: item.description };
          });
      });
    }
  }, [lookups, ifsp.reasonForDelay.id]);

  const updateIfspContext = (data: IfspContextDataType) => {
    setIfsp(data);
  };

  const updateInitialIfspContext = (data: IfspContextDataType) => {
    setInitialIfsp(data);
  };

  const updateIfspForReport = (data: IfspContextDataType) => {
    setInitialIfspForReports(data);
  };
  const updateIfspTouched = (touched: boolean) => {
    setIfspTouched(touched);
  };
  const updateOutComesFieldFlag = (data: boolean) => {
    setIsAllOutComesUpdated(data);
  };

  const updateOutComesStartDateFlag = (data: boolean) => {
    setIsOutComeStartDateHasIssue(data);
  };

  const updateOutComesEndDateFlag = (data: boolean) => {
    setIsOutComeEndDateHasIssue(data);
  };

  const UpdateLeaReferralDateOutOfRange = (data: boolean) => {
    setLeaReferralDateOutOfRange(data);
  };

  const UpdateTpcMeetingDateOutOfRange = (data: boolean) => {
    setTpcMeetingDateOutOfRange(data);
  };
  const updateTransitionOutOfRange = (data: boolean) => {
    setTransitionDateOutOfRange(data);
  };

  const updateIsTransitionDateReqired = (data: boolean) => {
    setIsTransitionDateReqired(data);
  };

  const updateIsTpcMeetingDateRequired = (data: boolean) => {
    setIsTpcMeetingDateRequired(data);
  };

  const updateIsLeaReferralDateRequired = (data: boolean) => {
    setIsLeaReferralDateRequired(data);
  };

  const updateIsOptOutNotificationDateRequired = (data: boolean) => {
    setIsOptOutNotificationDateRequired(data);
  };

  const handleCloseSnakBar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  useEffect(() => {
    if (
      _.isEqual(
        initialIfspForReports.ifspTeam.forms.dailyRoutines,
        ifsp.ifspTeam.forms.dailyRoutines,
      )
    ) {
      setIsDailyRoutineUpdated(false);
    } else {
      setIsDailyRoutineUpdated(true);
    }
  }, [ifsp.ifspTeam.forms.dailyRoutines]);

  useEffect(() => {
    if (
      _.isEqual(
        initialIfspForReports.ifspTeam.forms.familyConcerns,
        ifsp.ifspTeam.forms.familyConcerns,
      )
    ) {
      setIsFamilyConcernsUpdated(false);
    } else {
      setIsFamilyConcernsUpdated(true);
    }
  }, [ifsp.ifspTeam.forms.familyConcerns]);

  useEffect(() => {
    if (
      _.isEqual(
        initialIfspForReports.ifspTeam.forms.healthSummary,
        ifsp.ifspTeam.forms.healthSummary,
      )
    ) {
      setIsHealthAndEligibilityUpdated(false);
    } else {
      setIsHealthAndEligibilityUpdated(true);
    }
  }, [ifsp.ifspTeam.forms.healthSummary]);

  useEffect(() => {
    if (
      _.isEqual(
        initialIfspForReports.ifspTeam.forms.developmentalSummary,
        ifsp.ifspTeam.forms.developmentalSummary,
      )
    ) {
      setIsDevelopmentSummaryUpdated(false);
    } else {
      setIsDevelopmentSummaryUpdated(true);
    }
  }, [ifsp.ifspTeam.forms.developmentalSummary]);

  useEffect(() => {
    if (_.isEqual(initialIfsp, ifsp)) {
      handleUpdateUnsavedChanges(false);
      setIfspTouched(false);
    } else {
      handleUpdateUnsavedChanges(true);
      setIfspTouched(true);
    }
  }, [ifsp, initialIfsp]);

  const contextValue = useMemo(
    () => ({
      ifsp,
      initialIfspForReports,
      updateIfspContext,
      updateIfspForReport,
      updateIfspTouched,
      updateOutComesFieldFlag,
      updateInitialIfspContext,
      ifspTouched,
      initialIfsp,
      employeeData,
      getIfsp,
      updateIsTransitionDateReqired,
      updateIsLeaReferralDateRequired,
      updateIsTpcMeetingDateRequired,
      updateIsOptOutNotificationDateRequired,
      UpdateLeaReferralDateOutOfRange,
      UpdateTpcMeetingDateOutOfRange,
      updateTransitionOutOfRange,
      updateOutComesStartDateFlag,
      updateOutComesEndDateFlag,
      isTransitionDateReqired,
      isLeaReferralDateRequired,
      isTpcMeetingDateRequired,
      tpcMeetingDateOutOfRange,
      isOptOutNotificationDateRequired,
      leaReferralDateOutOfRange,
      transitionDateOutOfRange,
      primarySettingCode,
      reasonData,
      activeReasonData,
      isDailyRoutineUpdated,
      isDevelopmentSummaryUpdated,
      isFamilyConcernsUpdated,
      isHealthAndEligibilityUpdated,
      isAllOutComesUpdated,
      isOutComeEndDateHasIssue,
      isOutComeStartDateHasIssue,
    }),
    [
      ifsp,
      initialIfspForReports,
      updateIfspContext,
      updateIfspForReport,
      updateIfspTouched,
      updateOutComesFieldFlag,
      updateInitialIfspContext,
      ifspTouched,
      initialIfsp,
      employeeData,
      getIfsp,
      updateIsTransitionDateReqired,
      updateIsLeaReferralDateRequired,
      updateIsTpcMeetingDateRequired,
      updateIsOptOutNotificationDateRequired,
      UpdateLeaReferralDateOutOfRange,
      UpdateTpcMeetingDateOutOfRange,
      updateTransitionOutOfRange,
      updateOutComesStartDateFlag,
      updateOutComesEndDateFlag,
      isTransitionDateReqired,
      isLeaReferralDateRequired,
      isTpcMeetingDateRequired,
      tpcMeetingDateOutOfRange,
      isOptOutNotificationDateRequired,
      leaReferralDateOutOfRange,
      transitionDateOutOfRange,
      primarySettingCode,
      reasonData,
      activeReasonData,
      isDailyRoutineUpdated,
      isDevelopmentSummaryUpdated,
      isFamilyConcernsUpdated,
      isHealthAndEligibilityUpdated,
      isAllOutComesUpdated,
      isOutComeEndDateHasIssue,
      isOutComeStartDateHasIssue,
    ],
  );

  return (
    <Box component="div">
      <SnackBarComponent
        open={open}
        handleClose={handleCloseSnakBar}
        successOrError={toastrVariable}
        labelId={toastrId}
        defaultMessageId={toastrDefaultMessage}
      />
      <IfspContext.Provider value={contextValue}>
        {children}
      </IfspContext.Provider>
    </Box>
  );
};

export default IfspContext;
